import type { CSSProperties } from "react";

import { Picture } from "@/components/ui/Picture";
import type { MediaItem } from "@/data/media";

/**
 * One photograph, from wherever it came from.
 *
 * A media row is either a file in client/src/assets/images with committed
 * AVIF/WebP derivatives, or a file somebody uploaded through the admin panel,
 * which has none. Every caller would otherwise have to know which, so this is
 * the one place that decides, and `Picture` handles the two cases.
 *
 * A row with neither an asset nor a URL renders nothing rather than throwing.
 * `getImage` throws on an unknown id on purpose, to stop a deploy with a hole
 * in it, but an editor who has half-created a media row should not be able to
 * take a public page down from the admin panel.
 */
export function MediaImage({
  item,
  fill = false,
  sizes,
  priority = false,
  className = "",
  style,
  objectPosition,
}: {
  item: MediaItem | null | undefined;
  fill?: boolean;
  sizes?: string;
  priority?: boolean;
  className?: string;
  style?: CSSProperties;
  objectPosition?: string;
}) {
  if (!item) return null;
  // Fail closed. Static fallback data must never put a consent-pending frame
  // back on the page after the public API correctly withheld its CMS row.
  if (item.needsConsentCheck && item.consentStatus !== "granted") return null;
  if (item.kind === "upload" && !item.url) return null;
  if (item.kind !== "upload" && !item.asset) return null;

  return (
    <Picture
      asset={item.asset}
      src={item.kind === "upload" ? item.url : undefined}
      variants={item.kind === "upload" ? item.variants : undefined}
      blurDataUrl={item.kind === "upload" ? item.blurDataUrl : undefined}
      width={item.width}
      height={item.height}
      alt={item.alt}
      fill={fill}
      sizes={sizes}
      priority={priority}
      className={className}
      style={style}
      objectPosition={objectPosition ?? item.objectPosition}
    />
  );
}
